import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useSidebarMenus from "../hooks/useSidebarMenus";
import Aside from "./Aside";
import Main from "./Main";
import { fetchDataFrom } from "../_helper/fetcher";
import { setAuth, selectRole, selectIsAuthenticated } from "../store/auth";
import { RoleUtils, ROLES } from "../constants/roles";

function Layout(props) {
  const [collapsed, setCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userRole = useSelector(selectRole);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  
  const handleCollapsedChange = (checked) => {
    setCollapsed(checked);
  };

  async function getAdmin() {
    try {
      const res = await fetchDataFrom("me");
      if (res?.user) {
        const user = res.user;
        const permissions = user.role?.permissions || [];
        
        const authBody = {
          userId: user.id,
          permissions: permissions,
          name: user.name,
          email: user.email,
          isSuperAdmin: permissions[0] === "*",
          authorized: true,
        };
        dispatch(setAuth(authBody));
      } else {
        navigate("/login");
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      navigate("/login");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    // Skip auth check for login page
    if (props.pathname === "/login") {
      setIsLoading(false);
      return;
    }
    
    getAdmin();
  }, [props.pathname]);

  // Redirect users to appropriate dashboard based on role
  useEffect(() => {
    if (!isLoading && isAuthenticated && userRole && props.pathname === "/") {
      const dashboardRoute = RoleUtils.getDashboardRoute(userRole);
      if (dashboardRoute !== "/login") {
        navigate(dashboardRoute, { replace: true });
      }
    }
  }, [isLoading, isAuthenticated, userRole, props.pathname, navigate]);

  const menus = useSidebarMenus();

  // Show loading spinner while checking authentication
  if (isLoading && props.pathname !== "/login") {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="app overflow-x-hidden overflow-y-hidden">
      <Aside collapsed={collapsed} setCollapsed={setCollapsed} menus={menus} />
      <Main handleCollapsedChange={handleCollapsedChange} props={props}>
        {props.children}
      </Main>
    </div>
  );
}

const withCheckingLogin = (Component) => {
  return ({ pathname, ...props }) => {
    if (pathname === "/login") return <main>{props.children}</main>;
    return <Component {...props} />;
  };
};

export default withCheckingLogin(Layout);
